import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ShieldCheck,
  AlertTriangle,
  XCircle,
  Download,
  RefreshCw,
  Play,
  Clock,
  Award,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api/client";
import { daysUntilCalendarDate, formatCalendarDate, formatInstantDay } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { safetyHomePath } from "../../lib/safety-routes";
import { useAuth } from "../../contexts/AuthContext";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from "../../lib/load-failure";
import { useInSafetyLayout } from "./SafetyLayout";

/* ────────────── types ────────────── */

type CertStatus = "valid" | "expiring_soon" | "expired";

interface Certification {
  id: string;
  course_id: string;
  course_title: string;
  status: CertStatus;
  certificate_number: string;
  issued_at: string;
  expires_at: string;
  days_remaining?: number;
}

interface AvailableCourse {
  id: string;
  title: string;
  validity_period_months: number;
  passing_score: number;
  questions_per_quiz: number;
}

interface MyTrainingData {
  certifications: Certification[];
  available_courses: AvailableCourse[];
}

/* ────────────── helpers ────────────── */

/** `issued_at` is an INSTANT (timestamptz): shown as the TENANT's day. */
const fmtDate = (d: string, timeZone: string | null) =>
  formatInstantDay(d, timeZone, { month: "short", day: "numeric", year: "numeric" }) ?? "—";

/** `expires_at` is day-precision, not an instant — see eslint.legacy-patterns.js. */
const fmtExpiry = (d: string) =>
  formatCalendarDate(d, { month: "short", day: "numeric", year: "numeric" }) ?? "—";

const countdownText = (cert: Certification, timeZone: string | null) => {
  if (!cert.expires_at) return null;
  const days = cert.days_remaining ?? daysUntilCalendarDate(cert.expires_at, timeZone);
  if (days == null) return null;
  if (days < 0) return `Expired ${Math.abs(days)} days ago`;
  if (days <= 60) return `in ${days} days`;
  return `in ${days} days`;
};

/* SATURATED SIGNAL FILLS WITH DARK INK, the badge idiom — not the pale washes
 * with a bright hue as ink, which read under 2:1 in light mode. The signal hues
 * do not follow the theme, so one dark ink holds in both.
 *
 * A COUNTDOWN WITH NO DAY COUNT IS SLATE, NOT GREEN. It used to fall through to
 * green, the colour that means "valid", for a date this screen could not read:
 * absence of evidence drawn as a clean bill of health. */
const countdownChipClass = (cert: Certification, timeZone: string | null) => {
  const days = cert.days_remaining ?? daysUntilCalendarDate(cert.expires_at, timeZone);
  if (days == null) return "bg-fi-signal-slate text-fi-signal-ink";
  if (days < 0) return "bg-fi-signal-red text-fi-signal-ink";
  if (days <= 60) return "bg-fi-signal-amber text-fi-signal-ink";
  return "bg-fi-signal-green text-fi-signal-ink";
};

/* Exhaustive over the enum, and a status the map does not know is a slate `—`,
 * never a guessed label — it used to render an unlabelled, uncoloured pill. */
const CERT_STATUS: Record<CertStatus, { label: string; fill: string }> = {
  valid: { label: "Valid", fill: "bg-fi-signal-green" },
  expiring_soon: { label: "Expiring Soon", fill: "bg-fi-signal-amber" },
  expired: { label: "Expired", fill: "bg-fi-signal-red" },
};
const UNKNOWN_CERT_STATUS = { label: "\u2014", fill: "bg-fi-signal-slate" };
const certStatus = (status: string) =>
  CERT_STATUS[status as CertStatus] ?? UNKNOWN_CERT_STATUS;

const validityLabel = (months: number) => {
  if (months === 12) return "1 Year";
  if (months === 24) return "2 Years";
  if (months === 36) return "3 Years";
  return `${months} months`;
};

/* ────────────── main component ────────────── */

const MyTrainingScreen = () => {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const { isFieldWorker } = useAuth();

  const inLayout = useInSafetyLayout();

  /* A FAILED READ IS NOT "NO CERTIFICATIONS". It used to be: `data` undefined,
   * `?? []`, and the page read "0 Active · 0 Expiring Soon · 0 Expired" over
   * "No certifications yet" — to a hand whose tickets the server simply could
   * not be asked about. The notice goes where the list would have been, and
   * the three counts dash. Data from an earlier successful read is still shown
   * if a refetch fails; it is a real answer, only an older one. */
  const trainingQuery = useQuery<MyTrainingData>({
    queryKey: ["my-training-certifications"],
    queryFn: () =>
      api.safety
        .myTrainingCertifications()
        .then(
          (r: any) =>
            r.data?.data ??
            r.data ?? { certifications: [], available_courses: [] }
        ),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data, isLoading } = trainingQuery;
  const loadFailure = data === undefined ? queryLoadFailure(trainingQuery) : null;

  const certs = data?.certifications ?? [];
  const availableCourses = data?.available_courses ?? [];

  const stats = useMemo(() => {
    const active = certs.filter((c) => c.status === "valid").length;
    const expiring = certs.filter((c) => c.status === "expiring_soon").length;
    const expired = certs.filter((c) => c.status === "expired").length;
    return { active, expiring, expired };
  }, [certs]);

  const handleDownload = async (cert: Certification) => {
    try {
      const res = await api.safety.downloadCertPdf(cert.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cert.certificate_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download certificate");
    }
  };

  /* Under `SafetyLayout` the band owns the one <h1> and the strip says which
   * page this is, so the page draws no header of its own. A field worker
   * reaches this same screen through their own route tree, where there is no
   * layout, and there it keeps the header it always had. */
  const body = (
    <div className="space-y-6">
      {/* Status summary */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-fieldiq-muted" aria-hidden="true" />
          <span className="text-sm text-foreground">
            <strong>{loadFailure ? "\u2014" : stats.active}</strong> Active
          </span>
        </div>
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-fieldiq-muted" aria-hidden="true" />
          <span className="text-sm text-foreground">
            <strong>{loadFailure ? "\u2014" : stats.expiring}</strong> Expiring Soon
          </span>
        </div>
        <div className="flex items-center gap-2">
          <XCircle className="w-4 h-4 text-fieldiq-muted" aria-hidden="true" />
          <span className="text-sm text-foreground">
            <strong>{loadFailure ? "\u2014" : stats.expired}</strong> Expired
          </span>
        </div>
      </div>

      {/* Certification cards */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          Loading your training data...
        </div>
      ) : loadFailure ? (
        <LoadFailureNotice
          error={queryFailureError(trainingQuery)}
          kind={loadFailure}
          subject="training records"
        />
      ) : certs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No certifications yet. Start a training course below.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {certs.map((cert) => (
            <div
              key={cert.id}
              className="bg-fieldiq-card border border-border rounded-xl p-5 space-y-3"
            >
              <div className="flex items-start justify-between">
                <h3 className="text-sm font-bold text-foreground">
                  {cert.course_title}
                </h3>
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-semibold uppercase border border-black/20 text-fi-signal-ink ${certStatus(cert.status).fill}`}
                >
                  {certStatus(cert.status).label}
                </span>
              </div>

              <div className="space-y-1.5">
                <div className="text-xs text-muted-foreground font-mono">
                  {cert.certificate_number}
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>
                    Issued: {fmtDate(cert.issued_at, timeZone)}
                  </span>
                  <span>
                    Expires: {fmtExpiry(cert.expires_at)}
                  </span>
                </div>
                {cert.expires_at && (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold ${countdownChipClass(cert, timeZone)}`}
                  >
                    <Clock className="w-3 h-3" />
                    {countdownText(cert, timeZone)}
                    {cert.status === "expiring_soon" && " \u26A0\uFE0F"}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => handleDownload(cert)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-border rounded-lg text-foreground hover:bg-secondary/50 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download Certificate
                </button>
                <button
                  onClick={() =>
                    navigate(`/safety/training/${cert.course_id}/take`)
                  }
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-border rounded-lg text-foreground hover:bg-secondary/50 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Retake
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Available Training */}
      {!isLoading && availableCourses.length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-fieldiq-accent" />
            Available Training
          </h2>
          <div className="bg-fieldiq-card border border-border rounded-xl overflow-hidden divide-y divide-border">
            {availableCourses.map((course) => (
              <div
                key={course.id}
                className="flex items-center justify-between px-4 py-3 hover:bg-secondary/30 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {course.title}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[10px] text-muted-foreground">
                      {validityLabel(course.validity_period_months)}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {course.passing_score}% to pass
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {course.questions_per_quiz} questions
                    </span>
                  </div>
                </div>
                <button
                  onClick={() =>
                    navigate(`/safety/training/${course.id}/take`)
                  }
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Play className="w-3.5 h-3.5" />
                  Start Training
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (inLayout) return body;

  return (
    <div className="min-h-screen pb-[var(--bottom-chrome)] bg-background">
      {/* Action bar */}
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Persona-dependent, and NOT a literal. This screen is registered
              * in all three route trees (a field worker reaches it at
              * /safety/my-training), but `/safety` is registered only in the
              * AppLayout one — so for a field worker this back arrow used to
              * resolve to their tree's catch-all and bounce them home instead
              * of back. Repointed rather than registering `/safety` in the
              * field trees: the rule is register when no field-specific variant
              * exists, repoint when one does, and `/field/safety` already
              * exists in both field trees. See `lib/safety-routes.ts`. */}
            <button
              onClick={() => navigate(safetyHomePath(isFieldWorker))}
              aria-label="Back to Safety"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-2xl font-bold text-foreground">My Training</h1>
          </div>
        </div>
      </div>

      <div className="px-6 py-4">{body}</div>
    </div>
  );
};

export default MyTrainingScreen;
